import React, { Component } from 'react';
import Text from './Text';
import NNZ from '../images/nominal_not_zoom.png';
import NZ from '../images/nominal_zoom.png';
import VOL from '../images/Vol.png';
import { NavLink } from 'react-router-dom';

const publicPath = "/";

const ModelDescription = <div id="Descript">

Welcome to part 3, time to train some neural networks.<br/>
<br/>
To initialize the model we need the following:<br/>
<br/>
<code>
model = Sequential()
</code>
<br/><br/>

We add one layer with return sequences and one final layer. You may play with the values of output_dim to test different results.<br/><br/>

<code>
model.add(LSTM(input_shape=(1,), input_dim=1, output_dim=15, return_sequences=True))<br/>
model.add(LSTM(input_shape=(1,), input_dim=1, output_dim=15, return_sequences=False))<br/>
model.add(Dense(1))<br/>
model.add(Activation('linear'))
</code>
<br/><br/>

Dense and Activation are a little bit complex to be explained here.
You can research them if you want. What you should know is that in this case “linear” is the best activation method.<br/><br/>

Now comes the real art: choosing the loss function and the optimizer.
Depending on the data the performance can vary a lot, you should always analyze the characteristics of your data to make a choice.
For the loss function we
are going to use mean absolute percentage error (complete list of options <a href="https://keras.io/losses/">here</a>)
and there is a reason why it is the best for forecasting using this data.<br/><br/>

Imagine we want to forecast the values 100 and 1000 and we use mean square error as a loss function.
The model’s output is 200, and 1100. The total difference between the forecasts and the actual values is |100-200| + |1000-1100| = 200 but the % difference is 100% and 10%.
In the financial world a prediction that is 100% off is pretty bad.<br/><br/>

Mean square error will minimize the nominal differences between the values and forecasts, 
that will help for very big values, but will be pretty bad for low values where the % difference gets pretty big,
and that would cause the model to under-perform at the beginning when prices are lower.
Using mean absolute percentage error will help to minimize the % difference across the entire exercise.<br/><br/>

The optimizer is an algorithm that tries to minimize the loss function. In this case I picked "rmsprop",
you are free to test different optimizers (list <a href="https://keras.io/optimizers/">here</a>). Details about the performance of different optimizers are explained in Part 5.
When building these models you should know how each parameter affects the result because not always you can try each one of them.
If you have data with hundreds of thousands or millions of observations, testing all of them would take days.<br/><br/>

<code>
model.compile(loss="mape", optimizer="rmsprop")
</code>
<br/><br/>

The last thing regarding to the model that we need to chose is the epochs and the batch size.
One epoch means that all the data will be analyzed by the model once.
The batch size is how many observations will be analyzed by the model at the same time.<br/><br/>

These values should be tuned together and also according to the hardware because the more epochs, or lower batch size,
the longer will the model take to be trained; also a very high number of epochs could cause overfiting, that is when the model adapts too well to the available data,
but not too well to the data that it has not seen (and that’s important for trading decisions).
For this example I’m going to use 200 epochs and a batch size of 50 but you can play with the values.
All the information will be saved to an object called "NN".<br/><br/>

<code>
NN = model.fit(X, y, epochs=200, batch_size=50, verbose=2, shuffle=False)
</code>
<br/><br/>

If you run the code up to this point, the model will train itself, this can take several minutes depending of your hardware and your configuration.
Using a GPU can drastically reduce the time it takes to finish. In my case using a 1060 GTX with Tensorflow,
the model configurations mentioned above would take around 4-5 minutes.<br/><br/>

After the model is done we are going to save many files for later analysis:<br/>
- BTC_Nominal_loss.csv to visualize the performance of the optimizer.<br/>
- BTC_Nominal_params.json for the parameters.<br/>
- BTC_Nominal.json for the details.<br/>
- BTC_Nominal.h5 for the weights.
<br/><br/>

Remember to add paths like '/home/user/files/BTC_nominal.json' or 'C:/users/user/downloads/BTC_nominal.h5' if you are not using a python project.<br/><br/>

<code>
pd.DataFrame(NN.history).to_csv("BTC_nominal_loss.csv")<br/>
model_params = json.dumps(NN.params)<br/>
with open("BTC_nominal_params.json", "w") as json_file:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;json_file.write(model_params)<br/>
&nbsp;<br/>
model_json = model.to_json()<br/>
with open("BTC_nominal.json", "w") as json_file:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;json_file.write(model_json)<br/>
&nbsp;<br/>
model.save_weights("BTC_nominal_weights.h5")
</code>
<br/><br/>

Now is time to use the model to make predictions using the data. This piece of code will predict next day's price using the last 20 observations.<br/><br/>

<code>
Predictions = [model.predict(np.asarray(df['Price'])[i:i+Obs].reshape(1, Obs, 1)) for i in range(len(df)-Obs)]<br/>
Predictions = [df['Price'].iloc[0]]*Obs + [val[0][0] for val in Predictions]
</code>
<br/><br/>

After the predictions are ready, create a column in the dataframe with the predictions and reverse the transformations of the data that were before.<br/><br/>

<code>
df['Predictions'] = Predictions<br/>
df['Price'] = sc.inverse_transform(df['Price'])<br/>
df['Predictions'] = sc.inverse_transform(df['Predictions'])
</code>
<br/><br/>

Let's make a chart to compare the predictions to the actual data.<br/><br/>

<code>
plt.xlabel('Date')<br/>
plt.ylabel('Price')<br/>
plt.plot(df['Price'], 'b', label='Price')<br/>
plt.plot(df['Predictions'], 'r', label='Prediction')<br/>
plt.legend(loc='upper left', shadow=True, fontsize='x-large')
</code>
<br/><br/>

<img src={NNZ} alt="No Zoom Nominal"/>
<br/>

And zoom in to see the last values with more detail.<br/><br/>

<img src={NZ} alt="Zoom Nominal"/>

<br/>

Seems pretty good but we are going to use a couple metrics to double check. One is the mean absolute percentage error between the price and the predictions in batches of 500 days.
The other metric we can use is how many times the price falls between a certain percentage of error close to the prediction,
for example if the prediction is 100 and the price next day is 108, we can say that the prediction got it right if the margin is +/-10%.<br/><br/>

<code>
def ratios(x, y, z):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;predictions = list(df['Predictions'])[:-1]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;price = list(df['Price'])[:-1]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;corrects = []<br/>
&nbsp;&nbsp;&nbsp;&nbsp;for i in range(len(predictions)-y, len(predictions)-z):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;if (predictions[i] * (1.0 + (x/100.0))) > price[i] > (predictions[i] * (1.0 - (x/100.0))):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;corrects.append(1.0)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;else:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;corrects.append(0.0)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;return np.average(corrects) * 100<br/>
&nbsp;<br/>
def mape(y_true, y_pred):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;y_true, y_pred = np.array(y_true), np.array(y_pred)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;return np.mean(np.abs((y_true - y_pred) / y_true)) * 100<br/>
&nbsp;<br/>
</code>
<br/>

After creating both functions we test them with many values:<br/><br/>

<code>
MAPE = [mape(df['Price'][-500:], df['Predictions'][-500:]),<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;mape(df['Price'][-1000:-500], df['Predictions'][-1000:-500]),<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;mape(df['Price'][-1500:-1000], df['Predictions'][-1500:-1000]),<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;mape(df['Price'][-2000:-1500], df['Predictions'][-2000:-1500])]<br/>
&nbsp;<br/>
Col1 = [ratios(15, i, i-500) for i in range(500, 2001, 500)]<br/>
Col2 = [ratios(10, i, i-500) for i in range(500, 2001, 500)]<br/>
Col3 = [ratios(5, i, i-500) for i in range(500, 2001, 500)]<br/>
&nbsp;<br/>
print pd.DataFrame([MAPE, Col1, Col2, Col3],<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;index=["Mape", "Ratio 15%", "Ratio 10%", "Ratio 5%"],<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;columns=['0-500', "500-1000", "1000-1500", "1500-2000"])<br/>

&nbsp;<br/>
</code>
<br/>

Here's my output:<br/><br/>
<mark>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
0-500&nbsp;&nbsp;&nbsp;500-1000&nbsp;&nbsp;1000-1500&nbsp;&nbsp;1500-2000<br/>
Mape&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;3.68662&nbsp;&nbsp;&nbsp;2.444172&nbsp;&nbsp;&nbsp;&nbsp;3.121574&nbsp;&nbsp;&nbsp;10.068924<br/>
Ratio 15%&nbsp;&nbsp;98.00000&nbsp;&nbsp;99.600000&nbsp;&nbsp;98.600000&nbsp;&nbsp;65.200000<br/>
Ratio 10%&nbsp;&nbsp;93.80000&nbsp;&nbsp;97.000000&nbsp;&nbsp;97.000000&nbsp;&nbsp;51.600000<br/>
Ratio 5%&nbsp;&nbsp;&nbsp;&nbsp;78.40000&nbsp;&nbsp;87.200000&nbsp;&nbsp;80.400000&nbsp;&nbsp;27.400000
</mark>
<br/><br/>

Let's analyze the results, remember that 0-500 means the 500 most recent observations.
The first column is mean average percentual error, the lower, the better.
The values are low except for the oldest 500 observations, visualizing the volatility will shed a light here.
The other 3 columns are % of times that next day's price fell within the prediction's confidence bands. Again the last 500 observations show evidence that they were harder to predict.
We proceed to create a column with the historic 30 day volatility (average 30 day standard deviation of returns)
and print the chart.<br/><br/>

<code>
df['Vol'] = (df['Price'].pct_change() * 100.0).rolling(30).std(ddof=0)<br/>
plt.xlabel('Date')<br/>
plt.ylabel('Volatility of Returns')<br/>
plt.plot(df.index, df['Vol'])<br/>
plt.legend(loc='upper center', shadow=True, fontsize='large')
</code>
<br/><br/>

<img src={VOL} alt="Volatility"/>

<br/>
Here we can see that volatility has 2 big peaks at the beggining and that's why the mean average percentual error there is so high.
Also the precision of the Ratios function is considerably lower during those days.
We can also see that bitcoin volatility is cyclical and that could help us later.<br/><br/>

Now we have to think how to monetize the model, check <NavLink to={`${publicPath}strategy`}>Part 4: First Trading Strategies</NavLink>.

</div>;

class Model extends Component {

  componentDidMount() {
    window.onscroll = function() {scrollFunction()};

    function scrollFunction() {
        if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
            document.getElementById("myBtn").style.display = "block";
        } else {
            document.getElementById("myBtn").style.display = "none";
        }
    }

    function findPos(obj) {
      var curtop = 0;
      if (obj.offsetParent) {
          do {
              curtop += obj.offsetTop;
          } while (obj = obj.offsetParent);
      return [curtop];
      }
    }

    document.getElementById("myBtn").onclick = function() {
      const   scrollHeight = window.scrollY,
              scrollStep = Math.PI / ( 1000 / 15 ),
              cosParameter = scrollHeight / 2;
      var     scrollCount = 0,
              scrollMargin,
              scrollInterval = setInterval( function() {
                  if ( window.scrollY > 10 ) {
                      scrollCount = scrollCount + 1;  
                      scrollMargin = cosParameter - cosParameter * Math.cos( scrollCount * scrollStep );
                      window.scrollTo( 0, ( scrollHeight - scrollMargin ) );
                  } 
                  else clearInterval(scrollInterval); 
              }, 15 );
    }

    const scrollHeight = window.scrollY - findPos(document.getElementById("myNavbar"))[0],
    scrollStep = Math.PI / ( 1000 / 15 ),
    cosParameter = scrollHeight / 2;
    var scrollCount = 0,
    scrollMargin,
    scrollInterval = setInterval( function() {
        if ( window.scrollY - findPos(document.getElementById("myNavbar"))[0] > 10 ) {
            scrollCount = scrollCount + 1;  
            scrollMargin = cosParameter - cosParameter * Math.cos( scrollCount * scrollStep );
            window.scrollTo( 0, ( scrollHeight - scrollMargin ) );
        } 
        else clearInterval(scrollInterval); 
    }, 15 );
  }

  render() {
    return (
        <div id="Data">
        <h1>Part 3: Designing And Running The Model</h1>
          <Text Searchable="Time" description={ModelDescription}/>
        </div>
    );
  }
}

export default Model;



// WEBPACK FOOTER //
// ./src/components/Model.js